import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Download, Clock } from "lucide-react";
import {
  useMyWorkerRecord,
  useMyTimesheets,
  useMyStats,
  useSubmitTimesheet,
} from "@/hooks/useWorkerPortal";
import { useIsMobile } from "@/hooks/use-mobile";
import { format, endOfWeek, parseISO } from "date-fns";
import { toast } from "sonner";

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const STATUS_COLORS: Record<string, string> = {
  submitted: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  paid: "bg-blue-100 text-blue-800",
  rejected: "bg-red-100 text-red-800",
  draft: "bg-gray-100 text-gray-700",
};

// ── Submit Timesheet Dialog ───────────────────────────────────────────────
function SubmitTimesheetDialog({
  open,
  onClose,
  worker,
}: {
  open: boolean;
  onClose: () => void;
  worker: any;
}) {
  const submit = useSubmitTimesheet();

  const defaultWeekEnding = format(
    endOfWeek(new Date(), { weekStartsOn: 1 }),
    "yyyy-MM-dd"
  );

  const [weekEnding, setWeekEnding] = useState(defaultWeekEnding);
  const [regularHours, setRegularHours] = useState("");
  const [otHours, setOtHours] = useState("");

  const project = worker?.personnel_project;
  const rateSchedule = worker?.rate_schedule;
  const totalHours = (parseFloat(regularHours) || 0) + (parseFloat(otHours) || 0);
  const estimatedPay =
    (parseFloat(regularHours) || 0) * (rateSchedule?.bill_rate_regular ?? 0) +
    (parseFloat(otHours) || 0) * (rateSchedule?.bill_rate_ot ?? 0);

  const handleSubmit = async () => {
    if (!worker) return;
    const reg = parseFloat(regularHours);
    const ot = parseFloat(otHours) || 0;
    if (!weekEnding) return toast.error("Please select a week ending date");
    if (isNaN(reg) || reg < 0) return toast.error("Enter valid regular hours");
    if (reg + ot <= 0) return toast.error("Total hours must be greater than 0");

    try {
      await submit.mutateAsync({
        worker_id: worker.id,
        personnel_project_id: project?.id ?? worker.personnel_project_id,
        company_id: worker.company_id,
        week_ending: weekEnding,
        regular_hours: reg,
        ot_hours: ot,
        rate_schedule_id: rateSchedule?.id,
      });
      toast.success("Timesheet submitted for approval");
      onClose();
      setRegularHours("");
      setOtHours("");
      setWeekEnding(defaultWeekEnding);
    } catch (e: any) {
      toast.error(e.message ?? "Failed to submit timesheet");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-4 w-4" /> Submit Timesheet
          </DialogTitle>
          <DialogDescription>
            {project?.name
              ? `Project: ${project.name}${project.project_number ? ` (${project.project_number})` : ""}`
              : "Submit your hours for the selected week."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="week-ending">Week Ending (Sunday)</Label>
            <Input
              id="week-ending"
              type="date"
              value={weekEnding}
              onChange={(e) => setWeekEnding(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="reg-hours">Regular Hours</Label>
              <Input
                id="reg-hours"
                type="number"
                min="0"
                step="0.5"
                placeholder="0.0"
                value={regularHours}
                onChange={(e) => setRegularHours(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ot-hours">OT Hours</Label>
              <Input
                id="ot-hours"
                type="number"
                min="0"
                step="0.5"
                placeholder="0.0"
                value={otHours}
                onChange={(e) => setOtHours(e.target.value)}
              />
            </div>
          </div>

          {totalHours > 0 && (
            <div className="rounded-md bg-muted/50 border p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Hours</span>
                <span className="font-medium">{totalHours.toFixed(1)}</span>
              </div>
              {estimatedPay > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Est. Pay</span>
                  <span className="font-medium">{fmt(estimatedPay)}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submit.isPending || totalHours <= 0}>
            {submit.isPending ? "Submitting…" : "Submit Timesheet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────
const WorkerHours = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data: worker, isLoading: wLoading } = useMyWorkerRecord();
  const { data: lines = [], isLoading: lLoading } = useMyTimesheets(worker?.id);
  const stats = useMyStats(worker?.id);
  const isMobile = useIsMobile();

  const isLoading = wLoading || lLoading;

  // Group lines by timesheet
  const byWeek = new Map<string, any[]>();
  for (const l of lines) {
    const key = l.timesheet?.id || l.id;
    if (!byWeek.has(key)) byWeek.set(key, []);
    byWeek.get(key)!.push(l);
  }

  const weeks = Array.from(byWeek.entries()).map(([tsId, tsLines]) => {
    const ts = tsLines[0]?.timesheet;
    return {
      id: tsId,
      weekEnding: ts?.week_ending,
      project: ts?.personnel_project?.name || "—",
      projectNumber: ts?.personnel_project?.project_number || "",
      status: ts?.status || "draft",
      regular: tsLines.reduce((s: number, l: any) => s + (l.regular_hours || 0), 0),
      ot: tsLines.reduce((s: number, l: any) => s + (l.ot_hours || 0), 0),
      total: tsLines.reduce((s: number, l: any) => s + (l.total_hours || 0), 0),
      amount: tsLines.reduce((s: number, l: any) => s + (l.total_billed || 0), 0),
    };
  });

  // CSV export
  const handleDownloadCSV = () => {
    const rows = [
      ["Week Ending", "Project", "Regular Hrs", "OT Hrs", "Total Hrs", "Amount", "Status"],
      ...weeks.map((w) => [
        w.weekEnding ? format(parseISO(w.weekEnding), "yyyy-MM-dd") : "",
        w.project,
        w.regular.toFixed(1),
        w.ot.toFixed(1),
        w.total.toFixed(1),
        w.amount.toFixed(2),
        w.status,
      ]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `timesheets-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">My Hours</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleDownloadCSV} disabled={weeks.length === 0} className="flex-1 sm:flex-none">
            <Download className="h-4 w-4 mr-1" /> Download CSV
          </Button>
          <Button size="sm" onClick={() => setDialogOpen(true)} className="flex-1 sm:flex-none">
            <Plus className="h-4 w-4 mr-1" /> Submit Timesheet
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Timesheet History</CardTitle>
        </CardHeader>
        <CardContent>
          {weeks.length === 0 ? (
            <p className="text-center py-10 text-muted-foreground">
              No timesheets yet. Click <strong>Submit Timesheet</strong> to get started.
            </p>
          ) : isMobile ? (
            <div className="space-y-3">
              {weeks.map((w) => (
                <div key={w.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">
                      {w.weekEnding ? format(parseISO(w.weekEnding), "MMM d, yyyy") : "—"}
                    </span>
                    <Badge className={`text-xs capitalize ${STATUS_COLORS[w.status] ?? ""}`}>
                      {w.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {w.project}
                    {w.projectNumber && <span className="ml-1 text-xs">({w.projectNumber})</span>}
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex gap-3 text-muted-foreground">
                      <span>RT: {w.regular.toFixed(1)}</span>
                      <span>OT: {w.ot.toFixed(1)}</span>
                      <span className="font-medium text-foreground">Total: {w.total.toFixed(1)}</span>
                    </div>
                    <span className="font-semibold">{fmt(w.amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Week Ending</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">RT</TableHead>
                  <TableHead className="text-right">OT</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {weeks.map((w) => (
                  <TableRow key={w.id}>
                    <TableCell>
                      {w.weekEnding ? format(parseISO(w.weekEnding), "MMM d, yyyy") : "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {w.project}
                      {w.projectNumber && (
                        <span className="text-muted-foreground ml-1 text-xs">({w.projectNumber})</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs capitalize ${STATUS_COLORS[w.status] ?? ""}`}>
                        {w.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{w.regular.toFixed(1)}</TableCell>
                    <TableCell className="text-right">{w.ot.toFixed(1)}</TableCell>
                    <TableCell className="text-right font-medium">{w.total.toFixed(1)}</TableCell>
                    <TableCell className="text-right font-medium">{fmt(w.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-6 text-sm text-muted-foreground">
        <span>
          This Week: <strong className="text-foreground">{stats.weekHours.toFixed(1)} hrs</strong>{" "}
          | {fmt(stats.weekEarned)}
        </span>
        <span>
          MTD: <strong className="text-foreground">{stats.monthHours.toFixed(1)} hrs</strong>{" "}
          | {fmt(stats.monthEarned)}
        </span>
        <span>
          YTD: <strong className="text-foreground">{stats.ytdHours.toFixed(1)} hrs</strong>{" "}
          | {fmt(stats.ytdEarned)}
        </span>
      </div>

      <SubmitTimesheetDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        worker={worker}
      />
    </div>
  );
};

export default WorkerHours;
